import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Product = Tables<'products'>
type ProductOption = Tables<'product_options'>

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>()
  const [product, setProduct] = useState<Product | null>(null)
  const [options, setOptions] = useState<ProductOption[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!productId) return
    async function load() {
      const [prodRes, optsRes] = await Promise.all([
        supabase.from('products').select('*').eq('id', productId!).single(),
        supabase.from('product_options').select('*').eq('product_id', productId!).eq('is_active', true).order('group_name').order('sort_order'),
      ])
      setProduct(prodRes.data)
      setOptions(optsRes.data || [])
      setLoading(false)
    }
    load()
  }, [productId])

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading product...</p>
  if (!product) return <p>Product not found</p>

  const optionGroups = options.reduce<Record<string, ProductOption[]>>((acc, opt) => {
    if (!acc[opt.group_name]) acc[opt.group_name] = []
    acc[opt.group_name].push(opt)
    return acc
  }, {})

  const windResistance = product.wind_resistance as Record<string, unknown> | null

  return (
    <div>
      <PageHeader
        title={product.name}
        description={`SKU: ${product.sku}${product.product_type ? ` · ${product.product_type.replace(/_/g, ' ')}` : ''}${product.catalog_page ? ` · Catalog p.${product.catalog_page}` : ''}`}
        actions={<Link to={`/gaviota/catalog/${product.id}/configure`} className={css.btnPrimary}>Configure Product</Link>}
      />

      <div className={css.grid2}>
        <div>
          <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
            {product.image_url ? (
              <img src={product.image_url} alt={product.name} style={{ width: '100%', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)' }} />
            ) : (
              <div style={{ height: 200, background: 'var(--color-bg-subtle)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 'var(--space-4)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="1"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
              </div>
            )}
            {product.description && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>{product.description}</p>}
          </div>

          {product.features && product.features.length > 0 && (
            <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Features</h3>
              <ul style={{ margin: 0, paddingLeft: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                {product.features.map((f, i) => (
                  <li key={i} style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5 }}>{f}</li>
                ))}
              </ul>
            </div>
          )}

          {product.certifications && product.certifications.length > 0 && (
            <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Certifications</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                {product.certifications.map((c, i) => (
                  <StatusBadge key={i} label={c} variant="success" />
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
            <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Specifications</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
              {product.base_price && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Base Price</span><span style={{ fontWeight: 600 }}>£{Number(product.base_price).toLocaleString()}</span></div>}
              {product.min_width_mm && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Width Range</span><span>{product.min_width_mm} – {product.max_width_mm} mm</span></div>}
              {product.min_height_mm && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Height Range</span><span>{product.min_height_mm} – {product.max_height_mm} mm</span></div>}
              {product.min_projection_mm && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Projection Range</span><span>{product.min_projection_mm} – {product.max_projection_mm} mm</span></div>}
              {windResistance?.standard != null && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Wind Standard</span><span>{String(windResistance.standard)}</span></div>}
            </div>
          </div>

          {Object.keys(optionGroups).length > 0 && (
            <div className={css.card}>
              <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Available Options</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
                {Object.entries(optionGroups).map(([group, opts]) => (
                  <div key={group}>
                    <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, marginBottom: 'var(--space-2)', textTransform: 'capitalize' }}>{group.replace(/_/g, ' ')}</h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
                      {opts.map(opt => (
                        <span key={opt.id} style={{ fontSize: 'var(--text-xs)', padding: 'var(--space-1) var(--space-3)', background: opt.in_stock === false ? 'var(--color-technical-muted)' : 'var(--color-bg-subtle)', borderRadius: 'var(--radius-2xl)', border: `1px solid ${opt.in_stock === false ? 'rgba(199,123,26,0.2)' : 'var(--color-border-subtle)'}` }}>
                          {opt.name}
                          {opt.price_delta ? ` (+£${opt.price_delta})` : ''}
                          {opt.in_stock === false && ' · Non-stock'}
                          {opt.surcharge_percent ? ` (+${opt.surcharge_percent}%)` : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
